import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2, RefreshCw } from 'lucide-react'
import { useCan } from '@/features/auth'
import {
  fetchAdminPaymentGateways,
  setPaymentGatewayEnabled,
  type AdminPaymentGateway,
} from '@/features/payments/adminApi'
import { PaymentGatewayRow } from './PaymentGatewayRow'

/**
 * Administración de pasarelas de pago del autoservicio (pagos-zeus-3): lista
 * las pasarelas EXTERNAS de Zeus y permite habilitar/deshabilitar cuáles se
 * ofrecen en el wizard y en el pago de facturas del portal. La visibilidad
 * vive en el portal-server (tabla propia); ocultar aplica al instante.
 *
 * Desde portal-correcciones-2 cada pasarela lleva además su **nombre público**
 * (el que ve el cliente al pagar): también es config propia del portal, así que
 * el `name` de Zeus no se toca ni cambia para el resto de los sistemas.
 */
export function PaymentGatewaysAdminPage() {
  const canManage = useCan('payments:manage')
  // `items === null` = cargando (evita setState síncrono dentro del efecto).
  const [items, setItems] = useState<AdminPaymentGateway[] | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const loading = items === null

  useEffect(() => {
    if (!canManage) return
    let alive = true
    fetchAdminPaymentGateways()
      .then((data) => {
        if (alive) setItems(data)
      })
      .catch(() => {
        if (alive) {
          setItems([])
          toast.error('No se pudieron cargar las pasarelas de pago')
        }
      })
    return () => {
      alive = false
    }
  }, [canManage, reloadKey])

  const toggle = async (gateway: AdminPaymentGateway) => {
    setSavingId(gateway.id)
    try {
      const { enabled } = await setPaymentGatewayEnabled(
        gateway.id,
        !gateway.enabled,
      )
      setItems((prev) =>
        (prev ?? []).map((g) => (g.id === gateway.id ? { ...g, enabled } : g)),
      )
      toast.success(
        enabled
          ? `${gateway.name} visible en el portal`
          : `${gateway.name} oculta en el portal`,
      )
    } catch {
      toast.error('No se pudo actualizar la pasarela')
    } finally {
      setSavingId(null)
    }
  }

  /** El nombre lo persiste la fila; aquí solo se refleja lo confirmado. */
  const applyPublicName = (id: number, publicName: string | null) => {
    setItems((prev) =>
      (prev ?? []).map((g) => (g.id === id ? { ...g, publicName } : g)),
    )
  }

  if (!canManage) {
    return (
      <div className="rounded-3xl border border-black/5 bg-white p-8 text-center">
        <h1 className="text-lg font-bold">Acceso restringido</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Esta sección es exclusiva del administrador del portal.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Pasarelas de pago</h1>
          <p className="text-sm text-muted-foreground">
            Elige qué pasarelas externas de Zeus se ofrecen en el registro y en
            el pago de facturas del portal, y con qué nombre las ve el cliente.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setItems(null)
            setReloadKey((k) => k + 1)
          }}
          className="inline-flex items-center gap-2 rounded-xl border border-black/10 px-3 py-2 text-sm font-semibold hover:border-black/25"
        >
          <RefreshCw className="size-4" /> Actualizar
        </button>
      </div>

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="size-4 animate-spin" /> Cargando pasarelas…
        </p>
      ) : (items ?? []).length === 0 ? (
        <div className="rounded-3xl border border-black/5 bg-white p-8 text-center text-sm text-muted-foreground">
          No hay pasarelas externas integradas en Zeus. Intégralas primero
          desde controlweb (Facturación → Pasarelas).
        </div>
      ) : (
        <ul className="space-y-3">
          {(items ?? []).map((g) => (
            <PaymentGatewayRow
              key={g.id}
              gateway={g}
              savingVisibility={savingId === g.id}
              onToggleVisibility={() => void toggle(g)}
              onPublicNameSaved={(publicName) =>
                applyPublicName(g.id, publicName)
              }
            />
          ))}
        </ul>
      )}
    </div>
  )
}
